import { ReactNode } from 'react';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';

interface IntegrationSetupOptionProps {
  /** What this way of setting the channel up is called, e.g. "Quickchat AI bot". */
  title: string;
  /** One line on what the user gets, and what it costs them to set up. */
  description: ReactNode;
  /** Short qualifier shown beside the title, e.g. "Recommended" / "Advanced". */
  badge?: string;
  badgeVariant?: 'primary' | 'secondary';
  children: ReactNode;
  'data-testid'?: string;
}

/**
 * One named way of setting a channel up, for the sheets that offer more than one.
 * A rule and a heading rather than a card, so nested rows and fields keep their own
 * borders and the two options read as siblings rather than as containers.
 */
export const IntegrationSetupOption = ({
  title,
  description,
  badge,
  badgeVariant = 'secondary',
  children,
  ...props
}: IntegrationSetupOptionProps) => (
  <div className="border-t border-border/60 pt-5 space-y-4" data-testid={props['data-testid']}>
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="m-0 font-semibold text-base text-foreground">{title}</h3>
        {badge ? (
          <Badge variant={badgeVariant} size="md" data-testid={`${props['data-testid']}-badge`}>
            {badge}
          </Badge>
        ) : null}
      </div>
      <p className="m-0 max-w-prose text-muted-foreground text-sm text-pretty">{description}</p>
    </div>
    {children}
  </div>
);

export default IntegrationSetupOption;
